/* eslint no-magic-numbers: ["error", { "ignore": [0,1,2] }] */
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import { saveAs } from 'file-saver'
import qs from 'query-string'

import { usePagination } from '../../../hooks/usePagination'
import sortDirection from '../../../constants/sortDirection'
import {
  downloadHabitatsReportFile,
  getHabitatsReportsRequest
} from '../../../api/reports'
import { showErrorToast } from '../../../utils/toast'
import { buildReportsExpandPath } from '../../../routing/constants'
import ExpandedReportsTable from './ExpandedReportsTable'
import {
  makeHabitatsTabColumns,
  makeHabitatsTabRow
} from './habitatsTabTable'
import {
  Dash,
  ReportsPaginationWrapper,
  ReportsTableWrapper,
  ReportTableTitle,
  ReportTableWrapper,
  RightButtons
} from '../style'
import Button from '../../../components/Button'
import Download from '../../../assets/icons/download-icon'
import Expand from '../../../assets/icons/expand-icon'
import Table from '../../../components/Table'
import ItemsPerPageDropdown from '../../../components/ItemsPerPageDropdown'
import Pagination from '../../../features/Pagination'
import { getUrlForAllFilters } from '../index'

const defaultPaginationState = {
  totalNumber: 0,
  resultsPerPage: 10,
  totalPages: 0,
  currentPage: 1,
  hasNext: true,
  hasPrevious: false
}

const defaultFilters = {
  siteFilters: {
    region: [],
    country: [],
    system: [],
    threats: [],
    assessmentStatus: [],
    kbaStatus: [],
    kbaClassification: [],
    kbaCriterias: [],
    dateOfMostRecentAssessment: [],
    url: ''
  },
  speciesFilters: {
    speciesList: [],
    classList: [],
    selectedSpecieOptions: []
  },
  ecosystemFilters: {
    ecosystemList: [],
    ecosystemRedListCategory: [],
    url: ''
  },
  ecologicalFilters: {
    ecoregionList: [],
    url: ''
  },
  showReports: false
}

const HabitatsReportsTab = ({
  filters,
  setFilters,
  setLoadingScreenMessage,
  isHabitatsLoading,
  setIsHabitatsLoading,
  isRunReportTriggered
}) => {
  const history = useNavigate()
  const params = useParams()
  const {
    pagination,
    setPagination,
    selectPage,
    nextPage,
    prevPage,
    nextSection,
    prevSection,
    changeResultsPerPage
  } = usePagination(defaultPaginationState)
  const [habitatsReports, setHabitatsReports] = useState([])
  const [habitatsSorting, setHabitatsSorting] = useState({
    sortBy: 'siteCode',
    sortDirection: sortDirection.ASC
  })
  const prevRunReportTriggered = useRef(isRunReportTriggered)

  const getHabitatsReports = async () => {
    if (isHabitatsLoading) {
      return
    }
    const habitatsPagination = {
      resultsPerPage: pagination.resultsPerPage,
      currentPage: pagination.currentPage
    }
    const sorting = {
      sortBy: habitatsSorting.sortBy,
      sortDirection: habitatsSorting.sortDirection
    }
    setIsHabitatsLoading(true)

    try {
      const response = await getHabitatsReportsRequest({
        queryStrings: getUrlForAllFilters(filters),
        pagination: qs.stringify(habitatsPagination),
        sorting: qs.stringify(sorting)
      })

      if (response.error) {
        setHabitatsReports([])
        setIsHabitatsLoading(false)
        setPagination(defaultPaginationState)
        localStorage.setItem('reportsFilters', JSON.stringify(defaultFilters))
        setFilters(defaultFilters)
        return showErrorToast(response.error, 'showBorder')
      }

      setHabitatsReports(response.habitats || [])
      setPagination(response.pagination || defaultPaginationState)
      setIsHabitatsLoading(false)
    } catch (err) {
      setHabitatsReports([])
      setIsHabitatsLoading(false)
      setPagination(defaultPaginationState)
      localStorage.setItem('reportsFilters', JSON.stringify(defaultFilters))
      setFilters(defaultFilters)
      return showErrorToast('Something went wrong', 'showBorder')
    }
  }

  const expandTable = () => {
    history(buildReportsExpandPath('habitats'))
  }

  useEffect(() => {
    if (!filters.showReports) {
      return
    }

    const runTriggered = prevRunReportTriggered.current !== isRunReportTriggered
    prevRunReportTriggered.current = isRunReportTriggered

    if (runTriggered && pagination.currentPage !== 1) {
      setPagination((prev) => {
        return { ...prev, currentPage: 1 }
      })
      return
    }

    getHabitatsReports()
  }, [
    pagination.currentPage,
    pagination.resultsPerPage,
    habitatsSorting.sortBy,
    habitatsSorting.sortDirection,
    isRunReportTriggered
  ])

  const downloadCSV = async () => {
    setIsHabitatsLoading(true)
    setLoadingScreenMessage('buildingReport')
    const queryString = getUrlForAllFilters(filters)

    try {
      const response = await downloadHabitatsReportFile(queryString)

      if (response.error || !response.blob) {
        showErrorToast('something go wrong')
        setLoadingScreenMessage('runningReport')
        return setIsHabitatsLoading(false)
      }

      const getDate = new Date().toLocaleDateString('en-GB').replaceAll('/', '_')
      saveAs(response.blob, `Sites_Habitats_report_${getDate}.xlsx`)

      setIsHabitatsLoading(false)
      setLoadingScreenMessage('runningReport')
    } catch (err) {
      showErrorToast('something go wrong')
      setLoadingScreenMessage('runningReport')
      setIsHabitatsLoading(false)
    }
  }

  if (params.title && habitatsReports.length !== 0) {
    return (
      <ExpandedReportsTable
        title={params.title}
        loading={isHabitatsLoading}
        rows={makeHabitatsTabRow(habitatsReports)}
        columns={makeHabitatsTabColumns(
          habitatsSorting,
          setHabitatsSorting,
          habitatsReports
        )}
        totalNumber={pagination.totalNumber}
        resultsPerPage={pagination.resultsPerPage}
        changeResultsPerPage={changeResultsPerPage}
        download={downloadCSV}
      />
    )
  }

  return (
    <div>
      <ReportTableWrapper>
        <ReportTableTitle>
          <FormattedMessage id='habitats' />
          <Dash>{'—'}</Dash>
          <FormattedMessage id='reports' />
        </ReportTableTitle>
        <RightButtons>
          <Button dataTest='download' height='32px' marginTop='0px' onClick={downloadCSV}>
            <Download />
            <FormattedMessage id='download' />
          </Button>
        </RightButtons>
      </ReportTableWrapper>
      <ReportsTableWrapper activeTab='siteDetails'>
        <Table
          columns={makeHabitatsTabColumns(
            habitatsSorting,
            setHabitatsSorting,
            habitatsReports
          )}
          rows={makeHabitatsTabRow(habitatsReports)}
          noClearFilter={true}
          noResultMessage='noHabitatsMatchFilteringCriteria'
          dataTest='habitatsTab-list'
          isLoading={isHabitatsLoading}
        />
      </ReportsTableWrapper>
      <ReportsPaginationWrapper>
        <ItemsPerPageDropdown
          currentPage={pagination.currentPage}
          resultsPerPage={pagination.resultsPerPage}
          totalNumber={pagination.totalNumber}
          onChange={changeResultsPerPage}
          dataTest='habitats-items-per-page'
          menuPlacement='top'
          alternativeOptions={true}
        />
        {pagination.totalPages > 1 && (
          <Pagination
            activePages={pagination.currentPage}
            numberOfPages={pagination.totalPages}
            nextPage={nextPage}
            prevPage={prevPage}
            nextSection={nextSection}
            prevSection={prevSection}
            selectPage={selectPage}
            dataTest='page-number'
          />
        )}
      </ReportsPaginationWrapper>
    </div>
  )
}

export default HabitatsReportsTab
